<template>
    <div class="form__field">
        <label
            v-if="label"
            class="input__label"
        >
            {{ $capitalize(label) }}
        </label>
        <div style="position: relative">
            <input
                class="input__field e2e-number-input"
                :class="{
                    customClass,
                    'input__field--error': errorMessage,
                }"
                :value="internalValue"
                v-bind="$attrs"
                @beforeinput="handleDecimalEdgeCases"
                @input="onInput"
                @blur="onBlur"
                @click="onClick"
                @keypress="onKeyPress"
                @keyup="readCursorPositionFromField"
                ref="input"
                :inputmode="inputmode"
            />
            <div
                v-if="unit"
                class="input__unit"
            >
                {{ unit }}
            </div>
        </div>
        <div
            v-if="errorMessage"
            class="message message--error"
        >
            {{ errorMessage }}
        </div>
    </div>
</template>

<script>
    const DEFAULT_VALUE = '';

    export default {
        name: 'NumberInput',
        emits: [
            'input',
            'blur',
            'valueChanged',
            'click',
        ],
        props: {
            value: {
                type: [Number, String],
                required: false,
                default: null,
            },
            name: {
                type: String,
                required: true,
            },
            label: {
                type: String,
                required: true,
            },
            unit: {
                type: String,
                default: null,
            },
            errorMessage: {
                type: String,
                required: false,
                default: null,
            },
            customClass: {
                type: String,
                required: false,
                default: null,
            },
            inputmode: {
                type: String,
                required: false,
                default: 'text',
            },
            shouldFillDecimals: {
                type: Boolean,
                required: false,
                default: false,
            },
            onlyIntegers: {
                type: Boolean,
                required: false,
                default: false,
            },
            max: {
                type: Number,
                required: false,
                // Setting a default max value so value can't go into exponential mode, causing weird bugs
                default: 99999999999999,
            },
        },
        data() {
            return {
                isDirty: false,
                internalValue: DEFAULT_VALUE,
                lastCursorPosition: 0,
            };
        },
        created() {
            // decimalSeparator depends on current locale!
            // 11.11 are just a probe number, it could be any other decimal
            this.decimalSeparator = this.$twoDecimals(11.11).toString().indexOf('.') !== -1 ? '.' : ',';
            this.wrongDecimalSeparator = this.decimalSeparator === '.' ? ',' : '.';
            this.internalValue = this.valueAsString(this.value);
        },
        computed: {
            internalValueAsNumber() {
                if (!this.internalValue) {
                    return this.internalValue;
                }
                const value = this.internalValue.replace(',', '.');
                return parseFloat(value);
            },
        },
        watch: {
            value(newValue) {
                let newValueString;
                if (typeof newValue === 'number') {
                    newValueString = newValue.toString().replace('.', this.decimalSeparator);
                } else {
                    newValueString = newValue;
                }

                if (this.shouldFillDecimals) {
                    newValueString = this.fillDecimals(newValueString);
                }

                this.internalValue = newValueString;
            },
        },
        methods: {
            onKeyPress(e) {
                const charCode = e.which ? e.which : e.keyCode;
                const comma = 44;
                const dot = 46;

                // eslint-disable-next-line sonarjs/no-collapsible-if
                if ([comma, dot].includes(charCode) && this.alreadyContainsDecimalSeparator()) {
                    // If we have decimals enabled, we allow duplicate separators as that logic will take care of them
                    // This allows to naturally write decimals and go over decimal separator by pressing the key
                    if (!this.shouldFillDecimals) {
                        e.preventDefault();
                    }
                }

                if (charCode > 31 && (charCode < 48 || charCode > 57) && ![comma, dot].includes(charCode)) {
                    e.preventDefault();
                }
            },
            handleDecimalEdgeCases(e) {
                if (!this.shouldFillDecimals) {
                    return;
                }

                // Disallow leading 0 so when it gets formatted we don't have to manually compensate
                // for the missing 0'es in lastCursorPosition
                if (e.data === '0' && this.lastCursorPosition === 0) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                }

                if (e.inputType === 'deleteContentForward' || e.inputType === 'deleteContentBackward') {
                    const lookAheadOffset = e.inputType === 'deleteContentForward' ? 0 : -1;

                    // Prevent default and only move the cursor when user is trying to delete the comma separator in any way
                    // This prevents decimal bleeding into the normal number when removing the decimal separator
                    if (
                        this.internalValue.charAt(this.lastCursorPosition + lookAheadOffset)
                        === this.decimalSeparator
                    ) {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        this.setCursorPosition(e.inputType === 'deleteContentForward' ? 1 : -1);
                    }
                }
            },
            onInput(e) {
                this.readCursorPositionFromField();
                this.isDirty = true;
                let targetValue = e.target.value;
                if (targetValue === undefined || Number.isNaN(parseFloat(targetValue))) {
                    targetValue = '';
                }

                targetValue = targetValue.replace(this.wrongDecimalSeparator, this.decimalSeparator);
                if (this.max && Number(targetValue) > this.max) {
                    this.internalValue = String(this.max);
                } else {
                    this.internalValue = this.trimTo2Decimals(targetValue);
                }

                this.$forceUpdate();
                if (this.onlyIntegers) {
                    this.internalValue = String(this.internalValue).replace(/^([\d]+)([^\d].*)?$/, '$1');
                }

                if (this.shouldFillDecimals) {
                    this.internalValue = this.fillDecimals(this.internalValue);
                    this.setCursorPosition();
                }

                if (!targetValue) {
                    this.$emit('input', null);
                    this.$emit('valueChanged', null, this.name);
                    return;
                }
                this.$emit('input', this.internalValueAsNumber);
                this.$emit('valueChanged', this.internalValueAsNumber, this.name);
            },
            onBlur() {
                this.$emit('blur', this.name, this.internalValueAsNumber, this.isDirty);
            },
            onClick(event) {
                this.readCursorPositionFromField();
                this.$emit('click', event);
            },
            focusInput() {
                this.$refs.input.focus();
            },
            alreadyContainsDecimalSeparator() {
                return this.internalValue.toString().includes(this.decimalSeparator);
            },
            trimTo2Decimals(value) {
                if (!value) {
                    return value;
                }
                const tokens = value.split(this.decimalSeparator);

                if (tokens[1] && tokens[1].length > 2) {
                    return value.slice(0, -(tokens[1].length - 2));
                }

                return value;
            },
            fillDecimals(value) {
                const numberParts = (value || DEFAULT_VALUE).split(this.decimalSeparator);
                const decimals = numberParts[1] ? `${numberParts[1].concat('00').slice(0, 2)}` : '00';
                return `${numberParts[0]}${this.decimalSeparator}${decimals}`;
            },
            readCursorPositionFromField() {
                this.lastCursorPosition = this.$refs.input.selectionEnd;
            },
            setCursorPosition(offset = 0) {
                // Wait for input to update with any pending mutations
                this.$nextTick(() => {
                    // We also update our last cursor position with the new offset
                    this.lastCursorPosition = this.lastCursorPosition + offset;
                    this.$refs.input.setSelectionRange(this.lastCursorPosition, this.lastCursorPosition);
                });
            },
            valueAsString(value) {
                if (value === undefined || value === null) {
                    return DEFAULT_VALUE;
                }
                // eslint-disable-next-line no-param-reassign
                value = value.toString().replace(this.wrongDecimalSeparator, this.decimalSeparator);
                return this.trimTo2Decimals(value);
            },
        },
    };
</script>
